import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { api, invitesApi, discipleshipApi } from '../api/client';
import { nativeShare } from '../utils/share';
import { useTranslation } from '../i18n';
import {
  RecipientModal, QuickAddRecipientsModal, InviteShareModal, Toast,
} from '../screens/PrayerScreen';

/**
 * RecipientsContext — one prayer-recipient list, shared by two sections.
 *
 * Recipients used to live inside PrayerLayout, which was fine while the
 * Recipients tab lived there too. Now the list is the People section while the
 * Prayer calendar still schedules against the same records, so two route trees
 * need it. They never mount together, so a per-layout fetch would not race —
 * but it would let the two views drift (add someone in People, switch to
 * Prayer, and the calendar's copy is stale until a reload).
 *
 * So the list is hoisted to a provider mounted in Layout, above both sections.
 * The add/edit/quick-add/invite modals are hoisted with it, because "Add a
 * person" is offered from both the Prayer FAB and the People screen and both
 * should open the same dialog.
 *
 * The provider sits above every screen but does NOT fetch on mount — Layout
 * already fires five requests at boot, and a user who never opens Prayer or
 * People should not pay for a sixth. Sections call `ensureLoaded()` when they
 * mount; it fetches exactly once.
 *
 * Prayer still owns its calendar. It subscribes via `onRecipientsChanged` so it
 * can refetch calendar events after a mutation here, rather than this provider
 * reaching into prayer-specific state.
 */

const RecipientsContext = createContext(null);

export function useRecipients() {
  const ctx = useContext(RecipientsContext);
  if (!ctx) throw new Error('useRecipients must be used inside <RecipientsProvider>');
  return ctx;
}

export function RecipientsProvider({ children }) {
  const { t } = useTranslation();

  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  // Modal state
  const [modalOpen, setModalOpen]     = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [quickAddOpen, setQuickAdd]   = useState(false);
  const [inviteSheet, setInviteSheet] = useState(null); // { recipient, url, defaultText }
  const [toast, setToast]             = useState('');

  // Mirrors `recipients` so callbacks can read the current list without taking
  // it as a dependency and churning their identity on every list change.
  const recipientsRef = useRef([]);
  const setList = useCallback((next) => {
    setRecipients((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      recipientsRef.current = value;
      return value;
    });
  }, []);

  // Subscribers notified after any mutation. A ref, not state, so subscribing
  // never re-renders the provider.
  const listenersRef = useRef(new Set());
  const onRecipientsChanged = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);
  const notify = useCallback(() => {
    listenersRef.current.forEach((fn) => { try { fn(); } catch {} });
  }, []);

  const reload = useCallback(async () => {
    try {
      const d = await api.get('/api/prayer');
      setList(d.recipients || []);
      setError('');
      return d.recipients || [];
    } catch {
      setError(t('prayer.failedToLoad'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [t, setList]);

  // First section to mount triggers the fetch; later mounts get the same
  // promise back, so a caller can await the list without re-requesting it.
  const loadPromiseRef = useRef(null);
  const ensureLoaded = useCallback(() => {
    if (!loadPromiseRef.current) loadPromiseRef.current = reload();
    return loadPromiseRef.current;
  }, [reload]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  // Returns the saved recipient and whether it was the user's first — the
  // Prayer section uses that to jump them to their freshly-built calendar.
  const saveRecipient = useCallback(async (payload, target) => {
    const wasEmpty = recipientsRef.current.length === 0;
    let saved;
    if (target) {
      const d = await api.put(`/api/prayer/${target.id}`, payload);
      saved = d.recipient;
      setList(rs => rs.map(r => (r.id === target.id ? saved : r)));
    } else {
      const d = await api.post('/api/prayer', payload);
      saved = d.recipient;
      setList(rs => [saved, ...rs]);
    }
    notify();
    return { recipient: saved, wasFirst: wasEmpty && !target };
  }, [notify, setList]);

  // Logging a prayer touches the prayer calendar, which Prayer owns — so these
  // write and then notify rather than trying to reconcile calendar state here.
  const prayToday = useCallback(async (id) => {
    await api.post(`/api/prayer/${id}/pray`);
    await reload();
    notify();
  }, [reload, notify]);

  const prayOnDate = useCallback(async (id, dateStr) => {
    await api.post(`/api/prayer/${id}/pray-on-date`, { date: dateStr });
    notify();
  }, [notify]);

  const deleteRecipient = useCallback(async (id) => {
    if (!window.confirm(t('prayer.removeConfirm'))) return false;
    await api.delete(`/api/prayer/${id}`);
    setList(rs => rs.filter(r => r.id !== id));
    notify();
    return true;
  }, [t, notify, setList]);

  // ── Modal openers ──────────────────────────────────────────────────────────

  const openAdd      = useCallback(() => { setEditTarget(null); setModalOpen(true); }, []);
  const openEdit     = useCallback((r) => { setEditTarget(r); setModalOpen(true); }, []);
  const openQuickAdd = useCallback(() => setQuickAdd(true), []);

  // ── Outreach ───────────────────────────────────────────────────────────────

  const invite = useCallback(async (recipient) => {
    const { url } = await invitesApi.create({ kind: 'invite', recipientId: recipient.id });
    // Refresh so the new InviteLink shows up on the row.
    reload();
    // Open the personalise-message sheet rather than sharing straight away —
    // the default text is stage-aware.
    const VALID = ['till', 'soil', 'seed', 'sprout', 'plant', 'harvest'];
    const stage = VALID.includes(recipient.witnessStage) ? recipient.witnessStage : 'till';
    const first = (recipient.name || '').trim().split(/\s+/)[0] || recipient.name || '';
    setInviteSheet({ recipient, url, defaultText: t(`prayer.witness.inviteTemplates.${stage}`, first) });
  }, [reload, t]);

  const sendInvite = useCallback(async (text) => {
    if (!inviteSheet) return;
    const result = await nativeShare({ title: t('prayer.witness.shareTitle'), text, url: inviteSheet.url });
    if (result === 'copied')      setToast(t('prayer.witness.linkCopied'));
    else if (result === 'shared') setToast(t('prayer.witness.inviteSent'));
    setInviteSheet(null);
  }, [inviteSheet, t]);

  const startDiscipling = useCallback(async (recipient) => {
    if (!recipient.linkedUserId) return;
    try {
      await discipleshipApi.request(recipient.linkedUserId, 'discipler');
      setToast(t('prayer.witness.discipleSent'));
    } catch (e) {
      setToast(e.message || t('prayer.witness.discipleNeedsConnection'));
    }
  }, [t]);

  const value = {
    recipients, setRecipients: setList, loading, error,
    ensureLoaded, reload, saveRecipient, deleteRecipient, prayToday, prayOnDate,
    openAdd, openEdit, openQuickAdd,
    invite, startDiscipling,
    setToast,
    onRecipientsChanged,
  };

  return (
    <RecipientsContext.Provider value={value}>
      {children}

      {modalOpen && (
        <RecipientModal
          recipient={editTarget}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
          onSave={(payload) => saveRecipient(payload, editTarget)}
          onStartDiscipling={startDiscipling} />
      )}
      {quickAddOpen && (
        <QuickAddRecipientsModal
          onClose={() => setQuickAdd(false)}
          onSaved={() => { setQuickAdd(false); reload().then(notify); }} />
      )}
      {inviteSheet && (
        <InviteShareModal
          recipient={inviteSheet.recipient}
          url={inviteSheet.url}
          defaultText={inviteSheet.defaultText}
          onShare={sendInvite}
          onClose={() => setInviteSheet(null)} />
      )}
      {toast && <Toast message={toast} onHide={() => setToast('')} />}
    </RecipientsContext.Provider>
  );
}
